
import { useState } from "react";
import { Tabs } from "@/components/ui/tabs";
import { RoutineTabHeader } from "./routine-list/RoutineTabHeader";
import { RoutineTabContent } from "./routine-list/RoutineTabContent";

interface RoutinesListProps {
  routines: any[];
  onRoutinesChange: () => void;
  onRemoveRoutine?: (routineId: string) => Promise<void>;
  showRemoveButton: boolean;
  planId: string;
  isPersonalPlan: boolean;
  canEdit: boolean;
  hideDaySelector?: boolean;
}

export const RoutinesList = ({
  routines,
  onRoutinesChange,
  onRemoveRoutine,
  showRemoveButton,
  planId,
  isPersonalPlan,
  canEdit,
  hideDaySelector,
}: RoutinesListProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showExerciseForm, setShowExerciseForm] = useState(false);

  const handleTabChange = (value: string) => {
    const newIndex = parseInt(value);
    console.log("🔄 Tab change requested:", {
      from: currentIndex,
      to: newIndex,
      routineCount: routines.length,
      preservingState: true
    });
    
    setCurrentIndex(newIndex);
    // Reset exercise form when switching tabs
    setShowExerciseForm(false);
  };

  const handleToggleExerciseForm = () => {
    console.log("🔄 Exercise form toggled:", {
      currentState: showExerciseForm,
      newState: !showExerciseForm,
      currentTab: currentIndex,
      preserveTab: true
    });
    setShowExerciseForm(!showExerciseForm);
  };

  const handleUnlinkRoutine = async (routineId: string) => {
    if (!onRemoveRoutine) return;
    // Cerrar el form ANTES de la mutación para que el árbol que monta hooks
    // por rutina sea estable durante el re-render que dispara la cache optimista.
    setShowExerciseForm(false);
    // No tocar currentIndex aquí: validCurrentIndex (más abajo) ya lo clampa
    // automáticamente al rango válido del nuevo array de rutinas. Hacer un
    // setCurrentIndex después del await provocaba un render extra con
    // distinto número de hooks => React error #310 (pantalla en blanco).
    await onRemoveRoutine(routineId);
  };

  // Enhanced function to handle changes while preserving active routine for exercise modifications
  const handleRoutinesChange = () => {
    console.log("🔄 RoutinesList - Processing optimized routine change:", {
      currentIndex,
      routineCount: routines.length,
      preservingActiveTab: true,
      operationType: "exercise_modification"
    });
    
    // For exercise-related changes (adding/editing exercises within existing routines),
    // we preserve the currentIndex to maintain the active tab
    // Only adjust if the current index is out of bounds (e.g., when routines are removed)
    if (currentIndex >= routines.length && routines.length > 0) {
      const newIndex = Math.max(0, routines.length - 1);
      console.log("🔄 RoutinesList - Adjusting index due to routine removal:", {
        oldIndex: currentIndex,
        newIndex,
        reason: "current_index_out_of_bounds"
      });
      setCurrentIndex(newIndex);
    }
    
    // Trigger the parent update with navigation preservation
    onRoutinesChange();
  };

  const currentRoutine = routines[currentIndex];

  console.log("🔍 DEBUG RoutinesList - Optimized State:", {
    routineCount: routines.length,
    currentIndex,
    showExerciseForm,
    isPersonalPlan,
    canEdit,
    currentRoutineId: currentRoutine?.id,
    navigationPreserved: true
  });

  if (!routines || routines.length === 0) {
    return (
      <div className="text-center py-8 text-white/60">
        No hay rutinas en este plan
      </div>
    );
  }

  // Ensure currentIndex is within bounds
  const validCurrentIndex = Math.min(currentIndex, routines.length - 1);
  const activeRoutine = routines[validCurrentIndex];

  return (
    <Tabs value={validCurrentIndex.toString()} onValueChange={handleTabChange}>
      <RoutineTabHeader
        routines={routines}
        currentIndex={validCurrentIndex}
        showExerciseForm={showExerciseForm}
        currentRoutine={activeRoutine}
        showRemoveButton={showRemoveButton}
        planId={planId}
        isPersonalPlan={isPersonalPlan}
        canEdit={canEdit}
        onTabChange={handleTabChange}
        onToggleExerciseForm={handleToggleExerciseForm}
        onRemoveRoutine={onRemoveRoutine}
        handleUnlinkRoutine={handleUnlinkRoutine}
        hideDaySelector={hideDaySelector}
      />

      {routines.map((routine, index) => (
        <RoutineTabContent
          key={routine.id}
          routine={routine}
          index={index}
          showExerciseForm={showExerciseForm && index === validCurrentIndex}
          onRoutinesChange={handleRoutinesChange}
          onRemoveRoutine={onRemoveRoutine}
          handleUnlinkRoutine={handleUnlinkRoutine}
          isPersonalPlan={isPersonalPlan}
          canEdit={canEdit}
        />
      ))}
    </Tabs>
  );
};
